import { useLazyPayersPassengersRetrieveQuery } from '@bambi/api-client/bambi';
import type { PayerPassenger } from '@bambi/api-client/schemas';
import { FormErrorMessage } from '@bambi/component-library/src/components/forms/FormErrorMessage/FormErrorMessage';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { createColumnHelper } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { PayerPassengerManageCell } from './PayerPassengerManageCell';
import { DataGrid } from 'src/common/DataGrid/DataGrid';
import { SectionHeading } from 'src/common/primitives/Section/SectionHeading';
import formatServerError from 'src/common/util/serverErrorFormatter';

const columnHelper = createColumnHelper<PayerPassenger>();

interface PayerPassengerDataGridProps {
  payerPassengerIds: string[];
  passengerId: string;
}
export function PayerPassengerDataGrid({
  payerPassengerIds,
  passengerId,
}: PayerPassengerDataGridProps) {
  const [fetchPayerPassenger] = useLazyPayersPassengersRetrieveQuery();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payerPassengers, setPayerPassengers] = useState<PayerPassenger[]>([]);

  const fetchAllPayerPassengers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payerPassengers = await Promise.all(
        payerPassengerIds.map((payerPassengerId) =>
          fetchPayerPassenger({
            payerOrganizationId: payerPassengerId,
            passengerId,
          }).unwrap()
        )
      );
      setPayerPassengers(payerPassengers);
    } catch (error) {
      setError(formatServerError(error));
    } finally {
      setLoading(false);
    }
  }, [fetchPayerPassenger, payerPassengerIds, passengerId]);

  useEffect(() => {
    void fetchAllPayerPassengers();
  }, [fetchAllPayerPassengers]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('payer_organization.display_name', {
        header: 'Payer',
      }),
      columnHelper.accessor('external_id', {
        header: 'External ID',
      }),
      columnHelper.accessor('authorization_id', {
        header: 'Authorization ID',
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Manage',
        cell: ({ row }) => (
          <PayerPassengerManageCell
            payerPassenger={row.original}
            onAfterSave={fetchAllPayerPassengers}
          />
        ),
      }),
    ],
    [fetchAllPayerPassengers]
  );

  if (loading) {
    return <LoadingIndicator />;
  }

  if (error) {
    return <FormErrorMessage>{error}</FormErrorMessage>;
  }

  if (payerPassengers.length === 0) {
    return null;
  }

  return (
    <>
      <hr className="mb-4 mt-6 border-gray-200" />
      <SectionHeading heading="Payer Passenger Information" />
      <DataGrid<PayerPassenger>
        tableId="payer-passenger"
        data={payerPassengers}
        columns={columns}
        includePagination={false}
      />
    </>
  );
}
